import * as React from 'react';

import {
  TimerAnchor,
  createTimerAnchor,
  getRemainingSeconds,
} from '~shared/utils/ntpTimer';
import {
  TimerIconState,
  deriveTimerIconState,
} from '~shared/utils/timerIconState';

import {
  ActivityTimersResult,
  StudentTimerData,
} from '../../../api/activities/activityTimersApi';

export type StudentTimerDisplayState = {
  state: TimerIconState;
  remainingSeconds: number;
  timerDurationSeconds: number;
  startedAt: string | null;
  finishedAt: string | null;
};

/**
 * Derives the display state for a timer in a finished activity (reports view).
 *
 * When the teacher ends the activity, `finishActivityTimers` bulk-sets
 * `finished_at` on ALL timers (running, paused, and expired), so every timer
 * arrives with server state `finished`. We disambiguate using:
 *
 *   - `studentFinishedQuiz`: whether the student actually finished their quiz
 *     (derived from `participant.finished` in the parent component).
 *   - `remainingSeconds`: time the student had left when the timer was frozen.
 *
 * Mapping:
 *   - finished + studentFinishedQuiz + remaining  → completed   (student finished with time left)
 *   - finished + !studentFinishedQuiz + remaining → ended-early  (teacher ended, student had time)
 *   - finished + 0 remaining                      → expired      (time ran out; studentFinishedQuiz
 *                                                                  may be true in Showbie because the
 *                                                                  student app auto-submits on expiry)
 *
 * The paused/expired/running cases below are defensive fallbacks — in practice
 * every timer will be `finished` once the activity ends.
 */
export function deriveReportTimerState(
  timer: StudentTimerData,
  studentFinishedQuiz: boolean
): StudentTimerDisplayState {
  const remaining = timer.remainingSeconds ?? 0;
  const base = {
    timerDurationSeconds: timer.timerDurationSeconds,
    startedAt: timer.createdAt,
    finishedAt: timer.finishedAt,
  };

  switch (timer.state) {
    case 'finished': {
      let state: TimerIconState;
      if (studentFinishedQuiz && remaining > 0) {
        state = 'completed';
      } else if (remaining > 0) {
        state = 'ended-early';
      } else {
        // Time ran out — show Time Expired regardless of whether the Showbie
        // student app auto-submitted (studentFinishedQuiz may be true).
        state = 'expired';
      }

      return { ...base, state, remainingSeconds: remaining };
    }
    case 'paused':
      return { ...base, state: 'ended-early', remainingSeconds: remaining };
    case 'expired':
      return { ...base, state: 'expired', remainingSeconds: 0 };
    case 'running': {
      // Edge case: backend hasn't processed the bulk-finish yet.
      return { ...base, state: 'ended-early', remainingSeconds: remaining };
    }
  }
}

/**
 * Derives the display state for a timer in a live activity.
 *
 * Running timers count down client-side using NTP-anchored timestamps to avoid
 * clock skew. When a student finishes their quiz, a PubNub StudentFinished event
 * sets `studentFinishedQuiz` to true, which flips their timer to `completed`
 * immediately.
 */
export function deriveLiveTimerState(
  timer: StudentTimerData,
  anchor: TimerAnchor | undefined,
  studentFinishedQuiz: boolean
): StudentTimerDisplayState {
  let remaining: number;

  if (timer.state === 'running') {
    remaining = anchor ? getRemainingSeconds(anchor) : 0;
  } else {
    remaining = timer.remainingSeconds ?? 0;
  }

  const base = {
    timerDurationSeconds: timer.timerDurationSeconds,
    startedAt: timer.createdAt,
    finishedAt: timer.finishedAt,
  };

  switch (timer.state) {
    case 'finished':
      // remaining > 0: student submitted before time ran out → Quiz Submitted.
      // remaining = 0: time ran out. In Showbie, the student app auto-submits on
      // timer expiry (handleTimerExpired → finishActivity), making studentFinishedQuiz
      // true even though no time was left. Always show Time Expired when remaining=0
      // to match the Socratic standalone experience.
      if (studentFinishedQuiz && remaining > 0) {
        return { ...base, state: 'completed', remainingSeconds: remaining };
      }
      return { ...base, state: 'expired', remainingSeconds: 0 };
    case 'paused':
      return { ...base, state: 'paused', remainingSeconds: remaining };
    case 'expired':
      return { ...base, state: 'expired', remainingSeconds: 0 };
    case 'running': {
      if (studentFinishedQuiz) {
        return { ...base, state: 'completed', remainingSeconds: remaining };
      }

      return {
        ...base,
        state: deriveTimerIconState(remaining, timer.timerDurationSeconds),
        remainingSeconds: remaining,
      };
    }
  }
}

/**
 * Builds a map of student UUID → display state for all timers in the response.
 *
 * Two icon states are never produced by this function:
 *   - "hidden": applied at render time when the "Show Timers" toggle is off
 *     (see `ResultsParticipantCell`).
 *   - "not-started": the implicit state for students with no entry in the map.
 *     The /timers endpoint only returns students who have started their timer,
 *     so absent students are rendered as not-started by the UI layer.
 */
function buildTimerStates(
  timers: StudentTimerData[],
  anchors: Map<string, TimerAnchor>,
  finishedStudentUuids: Set<string>,
  activityFinished: boolean
): Map<string, StudentTimerDisplayState> {
  const stateMap = new Map<string, StudentTimerDisplayState>();

  for (const timer of timers) {
    const studentFinishedQuiz = finishedStudentUuids.has(timer.userUuid);

    const displayState = activityFinished
      ? deriveReportTimerState(timer, studentFinishedQuiz)
      : deriveLiveTimerState(
          timer,
          anchors.get(timer.userUuid),
          studentFinishedQuiz
        );

    stateMap.set(timer.userUuid, displayState);
  }

  return stateMap;
}

/**
 * Hook that transforms raw timer API data into a map of display states.
 *
 * Fetches are event-driven (PubNub invalidations), not polled. This hook
 * handles the client-side concerns:
 *   - Creates NTP anchors for running timers so countdowns  stay accurate.
 *   - Ticks every second while any timer is actively counting down.
 *   - Delegates to `deriveReportTimerState` or `deriveLiveTimerState`
 *     depending on whether the activity has finished.
 */
export function useStudentTimerStates(
  timersResult: ActivityTimersResult | undefined,
  finishedStudentUuids: Set<string>,
  activityFinished: boolean
): Map<string, StudentTimerDisplayState> {
  const [stateMap, setStateMap] = React.useState<
    Map<string, StudentTimerDisplayState>
  >(() => new Map());

  const anchorsRef = React.useRef<Map<string, TimerAnchor>>(new Map());

  React.useEffect(() => {
    if (!timersResult) {
      setStateMap((prev) => (prev.size === 0 ? prev : new Map()));
      anchorsRef.current = new Map();
      return;
    }

    const { data, networkLatencyMs } = timersResult;
    const newAnchors = new Map<string, TimerAnchor>();

    for (const timer of data.timers) {
      if (timer.state === 'running' && timer.expiresAt) {
        newAnchors.set(
          timer.userUuid,
          createTimerAnchor({
            expiresAt: timer.expiresAt,
            serverTime: data.serverTime,
            networkLatencyMs,
          })
        );
      }
    }

    anchorsRef.current = newAnchors;
    setStateMap(
      buildTimerStates(
        data.timers,
        newAnchors,
        finishedStudentUuids,
        activityFinished
      )
    );

    const hasActiveCountdowns = data.timers.some(
      (t) => t.state === 'running' && !finishedStudentUuids.has(t.userUuid)
    );
    if (!hasActiveCountdowns) {
      return;
    }

    const intervalId = setInterval(() => {
      setStateMap(
        buildTimerStates(
          data.timers,
          anchorsRef.current,
          finishedStudentUuids,
          activityFinished
        )
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timersResult, finishedStudentUuids, activityFinished]);

  return stateMap;
}
